import { useContext, useMemo, useState } from "react";
import { ParticipantContextProps } from "../../../../models/contexts/participant-context.model";
import { ParticipantContext } from "../../../../contexts/Participant.context";
import UiTable from "../../../../components/table/Table.component";
import {
	TableColumnTemplate,
	TableColumn,
} from "../../../../models/components/table/table-column.model";
import { AppointmentRow } from "../../../../models/pages/appointments/appointments.model";
import { TablePagination } from "../../../../models/components/table/table-pagination.model";
import { TableMessages } from "../../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../../models/components/field-message.model";
import AppointmentDetails from "../../../../components/appointments/AppointmentDetails";
import { PastAppointmentRow } from "../../../../models/pages/appointments/past-appointment.model";
import { useAppointmentsTypes } from "../../../../hooks/useAppointmentsTypes";
import { usePastAppointments } from "../../../../hooks/usePastAppointments";

const ParticipantPastAppointments = () => {
	const participantContext = useContext<ParticipantContextProps>(ParticipantContext);

	/* Table */

	// Columns
	const columns: TableColumn[] = [
		{
			field: "type",
			title: "APPOINTMENTS.TABLE.COLUMNS.appointmentType",
			template: TableColumnTemplate.TRANSLATE_PREFIX,
			templateOptions: { translationPrefix: "ENUMS.APPOINTMENTS_TYPE" },
			sortable: true,
		},
		{
			field: "datetime",
			title: "APPOINTMENTS.TABLE.COLUMNS.dateOn",
			sortable: true,
		},
		{
			field: "duration",
			title: "APPOINTMENTS.TABLE.COLUMNS.duration",
			sortable: true,
		},
		{
			field: "expandCollapse",
			template: TableColumnTemplate.EXPANDER,
			style: {
				minWidth: "120px",
			},
		},
	];

	// Pagination
	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "datetime",
		sortOrder: 1,
		search: undefined,
		filters: undefined,
	});

	// Pagination
	const onPagination = async (event: TablePagination): Promise<void> => {
		const newPagination: TablePagination = {
			...pagination,
			page: event.page,
			rows: event.rows,
			sortField: event.sortField,
			sortOrder: event.sortOrder,
			first: event.first,
		};
		setPagination(newPagination);
	};

	/**
	 * DATA
	 */

	const {
		data: appointments,
		isLoading,
		isError,
	} = usePastAppointments(participantContext, pagination);
	const { data: appointmentTypes = [] } = useAppointmentsTypes();

	// Rows
	const rows = useMemo(() => {
		if (!appointments) return [];

		return appointments
			.filter((x) => new Date(x.datetime).getTime() < new Date().getTime())
			.map((item) => {
				return new PastAppointmentRow(item, appointmentTypes);
			});
	}, [appointments, appointmentTypes]);

	// Messages
	const messages = new TableMessages();
	const message: UiFieldMessageProps = useMemo(() => {
		if (isLoading) {
			return {
				severity: FieldMessageSeverity.LOADING,
				label: messages.loading,
			};
		}

		if (isError) {
			return {
				severity: FieldMessageSeverity.DANGER,
				label: messages.error,
			};
		}

		if (!appointments || appointments.length === 0) {
			return {
				severity: FieldMessageSeverity.INFO,
				label: messages.empty,
			};
		}

		return {
			severity: FieldMessageSeverity.INFO,
			label: "",
		};
	}, [isLoading, isError, appointments, messages]);

	// Table template: expanded
	const templateExpanded = (row: AppointmentRow): React.ReactNode => (
		<AppointmentDetails row={row} />
	);

	return (
		<UiTable
			useAsCard={false}
			dataKey="id"
			columns={columns}
			value={rows}
			message={message}
			pagination={pagination}
			paginationFn={onPagination}
			expandedTemplate={templateExpanded}
		/>
	);
};
export default ParticipantPastAppointments;
